import { VectorTile } from '@mapbox/vector-tile';
import { OrbitControls } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Button, InlineNotification, Loading } from '@carbon/react';
import classNames from 'classnames';
import { Date } from 'components/date';
import { PageHeader } from 'components/page-header';
import { Field } from 'components/field';
import { useAtomValue, useSetAtom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { subscribe } from 'lib/pusher';
import { throttle } from 'lodash-es';
import Protobuf from 'pbf';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls as ThreeOrbitControls } from 'three/examples/jsm/controls/OrbitControls';

import style from './geological-map.module.scss';
import { fillableHoles, latticeResolution, lonLatToTile } from './utils';
import { format0, format1 } from 'utils/number';
import { Delete, Edit } from '@carbon/react/icons';
import {
  EditStructureGridModal,
  StructureGridEditData,
} from '../edit-structure-grid/edit-structure-grid-modal';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { StructureGridResponse } from 'types/api-responses';
import { DeleteStructureGridModal } from '../delete-structure-grid/delete-structure-grid';
import StructureGridValidityPopover from 'components/structure-grids/structure-grid-validity-popover';
import { Events as EventsIcon } from 'components/icons';
import { StructureGridExtractionStatus } from 'components/structure-grid-extraction-status/structure-grid-extraction-status';
import Compass from 'components/compass/compass';

// Interface definitions remain the same

interface StructureGridDetailsProps {
  data: StructureGridResponse;
}

interface LabelData {
  text: string;
  position: [number, number, number];
}

interface FeatureProperties {
  elevation?: number;
  ELEVATION?: number;
  elev?: number;
  Elev?: number;
  min_val?: number;
  max_val?: number;
  min?: number;
  max?: number;
}

interface GridParams {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
  minElevation: number;
  maxElevation: number;
  gridSize: number;
  zoom: number;
  tileSize: number;
  /** Cells across the lattice the surface is resampled onto. See `latticeResolution`. */
  resolution: number;
}

// Type for structure grid data with guaranteed coordinate values
type StructureGridCoordinates = StructureGridResponse & {
  minLongitude: string;
  maxLongitude: string;
  minLatitude: string;
  maxLatitude: string;
  minElevation: string;
  maxElevation: string;
};

// StructureGridDetails component remains unchanged
export const StructureGridDetails: React.FC<StructureGridDetailsProps> = ({
  data,
}) => {
  return (
    <div className={style.detailsFields}>
      <Field label="Basin" value={data.basinName} />
      <Field label="Grid Surface" value={data.gridSurface} />
      <Field label="Geologic Interval" value={data.geologicInterval} />
      <Field label="Source Projection" value={data.sourceProjection} />
      <Field label="Projection" value={data.projection} />
      <Field label="Data Owner" value={data.owner} />
      <Field label="Data Source" value={data.source} />
      <Field
        label="Created At"
        value={<Date date={data.createdAt} fallback="-" />}
      />
      <Field
        label="Source Created At"
        value={<Date date={data.sourceCreatedAt} fallback="-" />}
      />
      <Field label="Processed" value={data.gridProcessed ? 'Yes' : 'No'} />
      <Field label="File Size" value={`${data.fileSizeMb} MB`} />
      <Field
        label="Grid File Name"
        value={data.blobName}
        className={style.gridFileName}
      />
      <Field
        label="Cell Size (W x H)"
        value={
          data.cellWidthFeet !== null &&
          data.cellHeightFeet !== null &&
          data.cellWidthFeet > 0 &&
          data.cellHeightFeet > 0
            ? `${format0(data.cellWidthFeet)} x ${format0(
                data.cellHeightFeet
              )} ft`
            : 'Unknown'
        }
      />
    </div>
  );
};

// GridLinesAndFrame component for grid lines and frame
const GridLinesAndFrame: React.FC<{ box: THREE.Box3 }> = ({ box }) => {
  const size = useMemo(() => box.getSize(new THREE.Vector3()), [box]);
  const offset = 5; // Grid offset from the model

  // Offset box for grid placement
  const gridBox = useMemo(() => {
    const offsetBox = box.clone();
    offsetBox.min.x -= offset;
    offsetBox.min.y -= offset;
    offsetBox.min.z -= offset;
    return offsetBox;
  }, [box, offset]);

  // Materials as props instead of memoized objects
  const gridMaterialProps = useMemo(
    () => ({
      color: 0xcccccc,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
      depthTest: true,
    }),
    []
  );

  // Border material props
  const borderMaterialProps = useMemo(
    () => ({
      color: 0x999999,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      depthTest: true,
      linewidth: 1.5,
    }),
    []
  );

  // Create border geometries for the grid edges
  const borderGeometries = useMemo(() => {
    const offsetBox = gridBox;

    // X-axis face border (YZ plane)
    const xBorderGeometry = new THREE.BufferGeometry();
    const xBorderVertices = new Float32Array([
      // Rectangle around the YZ plane
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.min.z,
      offsetBox.min.x,
      offsetBox.max.y,
      offsetBox.min.z,

      offsetBox.min.x,
      offsetBox.max.y,
      offsetBox.min.z,
      offsetBox.min.x,
      offsetBox.max.y,
      offsetBox.max.z,

      offsetBox.min.x,
      offsetBox.max.y,
      offsetBox.max.z,
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.max.z,

      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.max.z,
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.min.z,
    ]);
    xBorderGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(xBorderVertices, 3)
    );

    // Y-axis face border (XZ plane)
    const yBorderGeometry = new THREE.BufferGeometry();
    const yBorderVertices = new Float32Array([
      // Rectangle around the XZ plane
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.min.z,
      offsetBox.max.x,
      offsetBox.min.y,
      offsetBox.min.z,

      offsetBox.max.x,
      offsetBox.min.y,
      offsetBox.min.z,
      offsetBox.max.x,
      offsetBox.min.y,
      offsetBox.max.z,

      offsetBox.max.x,
      offsetBox.min.y,
      offsetBox.max.z,
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.max.z,

      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.max.z,
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.min.z,
    ]);
    yBorderGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(yBorderVertices, 3)
    );

    // Z-axis face border (XY plane)
    const zBorderGeometry = new THREE.BufferGeometry();
    const zBorderVertices = new Float32Array([
      // Rectangle around the XY plane
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.min.z,
      offsetBox.max.x,
      offsetBox.min.y,
      offsetBox.min.z,

      offsetBox.max.x,
      offsetBox.min.y,
      offsetBox.min.z,
      offsetBox.max.x,
      offsetBox.max.y,
      offsetBox.min.z,

      offsetBox.max.x,
      offsetBox.max.y,
      offsetBox.min.z,
      offsetBox.min.x,
      offsetBox.max.y,
      offsetBox.min.z,

      offsetBox.min.x,
      offsetBox.max.y,
      offsetBox.min.z,
      offsetBox.min.x,
      offsetBox.min.y,
      offsetBox.min.z,
    ]);
    zBorderGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(zBorderVertices, 3)
    );

    return { xBorderGeometry, yBorderGeometry, zBorderGeometry };
  }, [gridBox]);

  // Grid faces
  const gridFaces = useMemo(() => {
    const divisions = 10;
    const offsetBox = gridBox;

    // Create grid for one face
    const createGridFace = (axis: 'x' | 'y' | 'z', position: number) => {
      const geometry = new THREE.BufferGeometry();
      const positions: number[] = [];

      for (let i = 0; i <= divisions; i++) {
        switch (axis) {
          case 'x': // YZ plane (left face)
            positions.push(
              position,
              offsetBox.min.y,
              offsetBox.min.z + i * (size.z / divisions),
              position,
              offsetBox.max.y,
              offsetBox.min.z + i * (size.z / divisions)
            );
            positions.push(
              position,
              offsetBox.min.y + i * (size.y / divisions),
              offsetBox.min.z,
              position,
              offsetBox.min.y + i * (size.y / divisions),
              offsetBox.max.z
            );
            break;
          case 'y': // XZ plane (bottom face)
            positions.push(
              offsetBox.min.x + i * (size.x / divisions),
              position,
              offsetBox.min.z,
              offsetBox.min.x + i * (size.x / divisions),
              position,
              offsetBox.max.z
            );
            positions.push(
              offsetBox.min.x,
              position,
              offsetBox.min.z + i * (size.z / divisions),
              offsetBox.max.x,
              position,
              offsetBox.min.z + i * (size.z / divisions)
            );
            break;
          case 'z': // XY plane (back face)
            positions.push(
              offsetBox.min.x + i * (size.x / divisions),
              offsetBox.min.y,
              position,
              offsetBox.min.x + i * (size.x / divisions),
              offsetBox.max.y,
              position
            );
            positions.push(
              offsetBox.min.x,
              offsetBox.min.y + i * (size.y / divisions),
              position,
              offsetBox.max.x,
              offsetBox.min.y + i * (size.y / divisions),
              position
            );
            break;
        }
      }

      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
      );
      return geometry;
    };

    // Create geometries for all three faces
    return [
      {
        axis: 'x' as const,
        position: offsetBox.min.x,
        geometry: createGridFace('x', offsetBox.min.x),
      },
      {
        axis: 'y' as const,
        position: offsetBox.min.y,
        geometry: createGridFace('y', offsetBox.min.y),
      },
      {
        axis: 'z' as const,
        position: offsetBox.min.z,
        geometry: createGridFace('z', offsetBox.min.z),
      },
    ];
  }, [gridBox, size]);

  return (
    <group renderOrder={-2}>
      {/* Main grid lines */}
      {gridFaces.map((face, index) => (
        <lineSegments key={`${face.axis}-${index}`} renderOrder={-2}>
          <primitive object={face.geometry} attach="geometry" />
          <lineBasicMaterial {...gridMaterialProps} />
        </lineSegments>
      ))}

      {/* Border lines */}
      <lineSegments renderOrder={-1}>
        <primitive
          object={borderGeometries.xBorderGeometry}
          attach="geometry"
        />
        <lineBasicMaterial {...borderMaterialProps} />
      </lineSegments>
      <lineSegments renderOrder={-1}>
        <primitive
          object={borderGeometries.yBorderGeometry}
          attach="geometry"
        />
        <lineBasicMaterial {...borderMaterialProps} />
      </lineSegments>
      <lineSegments renderOrder={-1}>
        <primitive
          object={borderGeometries.zBorderGeometry}
          attach="geometry"
        />
        <lineBasicMaterial {...borderMaterialProps} />
      </lineSegments>
    </group>
  );
};

// Custom hook for creating a label texture
function useLabelTexture(text: string) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [texture, setTexture] = useState<THREE.CanvasTexture | null>(null);

  useEffect(
    () => {
      // Create canvas if it doesn't exist
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = 240;
      canvas.height = 120;
      ctx.fillStyle = 'black';
      ctx.font = '32px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillText(text, canvas.width - 25, canvas.height / 2);

      const newTexture = new THREE.CanvasTexture(canvas);
      setTexture(newTexture);

      return () => {
        if (texture) {
          texture.dispose();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text]
  );

  return texture;
}

// Individual label sprite component
const LabelSprite: React.FC<{ label: LabelData }> = ({ label }) => {
  const texture = useLabelTexture(label.text);

  if (!texture) return null;

  return (
    <sprite position={label.position} scale={[10, 5, 1]}>
      <spriteMaterial map={texture} />
    </sprite>
  );
};

// Elevation labels component
const ElevationLabels: React.FC<{
  box: THREE.Box3;
  minElevation: number;
  maxElevation: number;
}> = ({ box, minElevation, maxElevation }) => {
  // Create offset box for label positioning
  const offsetBox = useMemo(() => {
    const newBox = box.clone();
    newBox.min.x -= 8; // Move labels further to the left
    return newBox;
  }, [box]);

  // Create sprites for elevation labels
  const labelData = useMemo<LabelData[]>(() => {
    const elevationRange = maxElevation - minElevation;
    const oneThirdElevation = minElevation + elevationRange / 3;
    const twoThirdsElevation = minElevation + (2 * elevationRange) / 3;

    const oneThirdY = offsetBox.min.y + (offsetBox.max.y - offsetBox.min.y) / 3;
    const twoThirdsY =
      offsetBox.min.y + (2 * (offsetBox.max.y - offsetBox.min.y)) / 3;

    return [
      {
        text: format1(minElevation),
        position: [offsetBox.min.x, offsetBox.min.y, offsetBox.min.z],
      },
      {
        text: format1(oneThirdElevation),
        position: [offsetBox.min.x, oneThirdY, offsetBox.min.z],
      },
      {
        text: format1(twoThirdsElevation),
        position: [offsetBox.min.x, twoThirdsY, offsetBox.min.z],
      },
      {
        text: format1(maxElevation),
        position: [offsetBox.min.x, offsetBox.max.y, offsetBox.min.z],
      },
    ];
  }, [offsetBox, minElevation, maxElevation]);

  return (
    <group userData={{ isElevationLabel: true }}>
      {labelData.map((label, index) => (
        <LabelSprite key={index} label={label} />
      ))}
    </group>
  );
};

const SurfaceMesh: React.FC<{
  geometry?: THREE.BufferGeometry;
  position?: [number, number, number];
  scale?: [number, number, number];
  minElevation?: number;
  maxElevation?: number;
}> = ({
  geometry,
  position = [0, 0, 0],
  scale = [1, 1, 1],
  minElevation,
  maxElevation,
}) => {
  if (!geometry || minElevation === undefined || maxElevation === undefined)
    return null;

  return (
    <mesh
      renderOrder={10}
      position={position}
      scale={scale}
      geometry={geometry}
    >
      <meshPhongMaterial
        vertexColors={true}
        side={THREE.DoubleSide}
        shininess={20}
        specular={new THREE.Color(0x111111)}
        flatShading={false}
        reflectivity={0.2}
        emissive={new THREE.Color(0x000000)}
      />
    </mesh>
  );
};

// Controls component
const SceneControls: React.FC<{
  onCompassRotation: (rotation: number) => void;
}> = ({ onCompassRotation }) => {
  const controlsRef = useRef<React.ElementRef<typeof OrbitControls>>(null);
  const threeControlsRef = useRef<ThreeOrbitControls | null>(null);

  // Memoized throttle function
  const throttledRotation = useMemo(
    () => throttle((rotation: number) => onCompassRotation(rotation), 100),
    [onCompassRotation]
  );

  // Use useFrame for smoother updates
  useFrame(() => {
    if (threeControlsRef.current) {
      const azimuthalAngle = threeControlsRef.current.getAzimuthalAngle();
      throttledRotation(azimuthalAngle);
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.03}
      zoomSpeed={0.5} // Slower zoom speed
      rotateSpeed={1.0} // Default rotation speed
      maxPolarAngle={Math.PI / 2}
      minPolarAngle={0}
      maxDistance={500}
      minDistance={50}
      makeDefault
      onUpdate={(self) => {
        threeControlsRef.current = self as unknown as ThreeOrbitControls;
      }}
    />
  );
};

// Lights component
const SceneLights: React.FC = () => (
  <>
    <ambientLight intensity={0.15} />
    <directionalLight position={[1, 2, 1]} intensity={0.4} />
    <directionalLight position={[-1.5, 1, -0.5]} intensity={0.3} />
    <spotLight
      position={[0, 300, 0]}
      angle={0.3}
      penumbra={0.2}
      intensity={0.2}
    />
    <hemisphereLight args={[0xffffff, 0x444444, 0.15]} />
  </>
);

// Scene camera setup
const SceneSetup: React.FC<{
  onInit: (camera: THREE.Camera) => void;
}> = ({ onInit }) => {
  const { camera } = useThree();

  useEffect(() => {
    camera.up.set(0, 1, 0);
    camera.lookAt(0, 0, 0);

    // Call onInit callback with camera reference
    onInit(camera);
  }, [camera, onInit]);

  return <camera position={[0, 300, -300]} />;
};

// Function to get color based on elevation
function useColorMap() {
  return useMemo(() => {
    // Color stops as a memoized constant
    const colorStops = [
      { position: 0.0, color: new THREE.Color(0x000088) },
      { position: 0.2, color: new THREE.Color(0x004488) },
      { position: 0.3, color: new THREE.Color(0x004455) },
      { position: 0.4, color: new THREE.Color(0x005533) },
      { position: 0.5, color: new THREE.Color(0x004400) },
      { position: 0.6, color: new THREE.Color(0x334400) },
      { position: 0.7, color: new THREE.Color(0x664422) },
      { position: 0.8, color: new THREE.Color(0x663300) },
      { position: 0.9, color: new THREE.Color(0x662200) },
      { position: 1.0, color: new THREE.Color(0x441100) },
    ];

    return (
      elevation: number,
      minElevation: number,
      maxElevation: number
    ): THREE.Color => {
      // Normalize elevation to 0-1 range
      const normalizedElevation =
        (elevation - minElevation) / (maxElevation - minElevation);

      // Find the right color stops
      let lowerStop = colorStops[0];
      let upperStop = colorStops[colorStops.length - 1];

      for (let i = 0; i < colorStops.length - 1; i++) {
        if (
          normalizedElevation >= colorStops[i].position &&
          normalizedElevation <= colorStops[i + 1].position
        ) {
          lowerStop = colorStops[i];
          upperStop = colorStops[i + 1];
          break;
        }
      }

      // Interpolate between the two color stops
      const t =
        (normalizedElevation - lowerStop.position) /
        (upperStop.position - lowerStop.position);

      // Interpolate RGB components
      const r = lowerStop.color.r + t * (upperStop.color.r - lowerStop.color.r);
      const g = lowerStop.color.g + t * (upperStop.color.g - lowerStop.color.g);
      const b = lowerStop.color.b + t * (upperStop.color.b - lowerStop.color.b);

      return new THREE.Color(r, g, b);
    };
  }, []);
}

// Custom hook for loading and caching geometry
function useGeometryLoader(
  data: StructureGridResponse | null,
  onLoadingChange: (loading: boolean) => void,
  onError: (error: string | null) => void
): {
  geometry: THREE.BufferGeometry | null;
  meshBox: THREE.Box3;
  surfacePosition: [number, number, number];
  surfaceScale: [number, number, number];
  elevationRange: { min: number; max: number };
} {
  // State for the surface mesh
  const [surfaceGeometry, setSurfaceGeometry] =
    useState<THREE.BufferGeometry | null>(null);
  const [surfacePosition, setSurfacePosition] = useState<
    [number, number, number]
  >([0, 0, 0]);
  const [surfaceScale, setSurfaceScale] = useState<[number, number, number]>([
    1, 1, 1,
  ]);
  const [meshBox, setMeshBox] = useState<THREE.Box3>(
    new THREE.Box3(
      new THREE.Vector3(-60, -30, -60),
      new THREE.Vector3(60, 30, 60)
    )
  );
  const [elevationRange, setElevationRange] = useState<{
    min: number;
    max: number;
  }>({ min: 0, max: 100 });

  // Use a ref for the tile cache
  const tileCache = useRef<Map<string, VectorTile>>(new Map());

  // Use a ref for the processing state
  const processingRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  const getColorForElevation = useColorMap();

  // Convert tile coordinates to longitude/latitude
  const tileToLonLat = useCallback((x: number, y: number, zoom: number) => {
    const n = Math.pow(2, zoom);
    const lon = (x / n) * 360 - 180;
    const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
    const lat = (latRad * 180) / Math.PI;
    return { lon, lat };
  }, []);

  const fetchMVTData = useCallback(
    async (url: string, x: number, y: number, z: number) => {
      if (!isMountedRef.current) return null;

      try {
        const cacheKey = `${x}-${y}-${z}`;
        // Check if this tile is already in our cache
        if (tileCache.current.has(cacheKey)) {
          return tileCache.current.get(cacheKey);
        }

        const tileUrl = url
          .replace('{x}', x.toString())
          .replace('{y}', y.toString())
          .replace('{z}', z.toString());

        const response = await fetch(tileUrl);
        if (!response.ok) {
          if (response.status === 404) {
            return null;
          }
          throw new Error(`Failed to fetch MVT: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        if (arrayBuffer.byteLength === 0) {
          return null;
        }

        if (!isMountedRef.current) return null;

        const vectorTile = new VectorTile(new Protobuf(arrayBuffer));

        // Store in cache
        tileCache.current.set(cacheKey, vectorTile);

        return vectorTile;
      } catch (error: unknown) {
        if (isMountedRef.current) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onError(`Failed to load MVT data: ${errorMessage}`);
        }
        return null;
      }
    },
    [onError]
  );

  // Process MVT data and convert to THREE.js geometry
  const processMVTData = useCallback(
    async (mvtUrl: string, gridParams: GridParams) => {
      if (!mvtUrl || !isMountedRef.current) {
        onError('No MVT URL provided');
        return null;
      }

      if (processingRef.current) {
        return null;
      }

      processingRef.current = true;
      onLoadingChange(true);
      onError(null);

      try {
        const { minLon, maxLon, minLat, maxLat, zoom, resolution } = gridParams;

        const minTile = lonLatToTile(minLon, maxLat, zoom);
        const maxTile = lonLatToTile(maxLon, minLat, zoom);

        // Validate tile bounds
        if (minTile.x > maxTile.x || minTile.y > maxTile.y) {
          throw new Error('Invalid tile bounds');
        }

        const elevationGrid: (number | null)[][] = Array(resolution)
          .fill(null)
          .map(() => Array(resolution).fill(null));
        const pointCounts: number[][] = Array(resolution)
          .fill(0)
          .map(() => Array(resolution).fill(0));
        const hasData: boolean[][] = Array(resolution)
          .fill(false)
          .map(() => Array(resolution).fill(false));

        // Prepare tiles to process in batches
        const tilesToProcess: { x: number; y: number }[] = [];
        for (let x = minTile.x; x <= maxTile.x; x++) {
          for (let y = minTile.y; y <= maxTile.y; y++) {
            tilesToProcess.push({ x, y });
          }
        }

        // Limit to max tiles for performance
        const maxTilesToFetch = 50;
        const limitedTiles = tilesToProcess.slice(0, maxTilesToFetch);

        let minElevation = Infinity;
        let maxElevation = -Infinity;
        let dataPointsProcessed = 0;
        let tilesWithData = 0;

        // Process tiles in batches for better performance
        const batchSize = 5; // Process 5 tiles at a time
        const batches = Math.ceil(limitedTiles.length / batchSize);

        for (let b = 0; b < batches && isMountedRef.current; b++) {
          const start = b * batchSize;
          const end = Math.min((b + 1) * batchSize, limitedTiles.length);
          const currentBatch = limitedTiles.slice(start, end);

          // Create promises for all tiles in the batch
          const tilePromises = currentBatch.map(({ x, y }) =>
            fetchMVTData(mvtUrl, x, y, zoom)
          );

          // Process all tiles in the batch in parallel
          const vectorTiles = await Promise.all(tilePromises);

          // Process each tile's data
          for (let i = 0; i < vectorTiles.length && isMountedRef.current; i++) {
            const vectorTile = vectorTiles[i];
            if (!vectorTile) continue;

            tilesWithData++;
            const { x, y } = currentBatch[i];

            // Process each layer in the tile
            for (const layerName in vectorTile.layers) {
              if (
                Object.prototype.hasOwnProperty.call(
                  vectorTile.layers,
                  layerName
                ) &&
                isMountedRef.current
              ) {
                const layer = vectorTile.layers[layerName];

                try {
                  for (
                    let j = 0;
                    j < layer.length && isMountedRef.current;
                    j++
                  ) {
                    const feature = layer.feature(j);
                    const properties = feature.properties as FeatureProperties;
                    const elevation =
                      properties.elevation ??
                      properties.ELEVATION ??
                      properties.elev ??
                      properties.Elev;

                    if (typeof elevation !== 'number' || isNaN(elevation))
                      continue;

                    minElevation = Math.min(minElevation, elevation);
                    maxElevation = Math.max(maxElevation, elevation);

                    try {
                      const geometry = feature.loadGeometry();
                      for (const ring of geometry) {
                        for (const point of ring) {
                          if (!isMountedRef.current) return null;

                          const tileX = x + point.x / layer.extent;
                          const tileY = y + point.y / layer.extent;
                          const { lon, lat } = tileToLonLat(tileX, tileY, zoom);

                          if (
                            lon < minLon ||
                            lon > maxLon ||
                            lat < minLat ||
                            lat > maxLat
                          ) {
                            continue;
                          }

                          const gridX = Math.floor(
                            ((lon - minLon) / (maxLon - minLon)) *
                              (resolution - 1)
                          );
                          const gridY = Math.floor(
                            ((lat - minLat) / (maxLat - minLat)) *
                              (resolution - 1)
                          );

                          if (
                            gridX >= 0 &&
                            gridX < resolution &&
                            gridY >= 0 &&
                            gridY < resolution
                          ) {
                            hasData[gridY][gridX] = true;
                            if (elevationGrid[gridY][gridX] === null) {
                              elevationGrid[gridY][gridX] = elevation;
                            } else {
                              elevationGrid[gridY][gridX] =
                                ((elevationGrid[gridY][gridX] ?? 0) *
                                  pointCounts[gridY][gridX] +
                                  elevation) /
                                (pointCounts[gridY][gridX] + 1);
                            }
                            pointCounts[gridY][gridX]++;
                            dataPointsProcessed++;
                          }
                        }
                      }
                    } catch (geometryError) {
                      continue; // Skip this feature but continue processing others
                    }
                  }
                } catch (layerError) {
                  continue; // Skip this layer but continue processing others
                }
              }
            }
          }
        }

        if (!isMountedRef.current) return null;

        if (tilesWithData === 0) {
          throw new Error(
            `No data found in ${limitedTiles.length} processed tiles`
          );
        }

        if (dataPointsProcessed === 0) {
          throw new Error('No valid data points found in processed tiles');
        }

        if (!isFinite(minElevation) || !isFinite(maxElevation)) {
          throw new Error('Invalid elevation range detected');
        }

        // Second pass: fill holes inside the surface.
        for (const { x, y, elevation } of fillableHoles(elevationGrid)) {
          elevationGrid[y][x] = elevation;
          hasData[y][x] = true;
        }

        if (!isMountedRef.current) return null;

        // Pre-allocate arrays for better performance
        const positions: number[] = [];
        const colors: number[] = [];
        const indices: number[] = [];
        const vertexMap = new Map<string, number>();

        // Create vertices only where we have data
        try {
          // First pass: create vertices
          for (let y = 0; y < resolution && isMountedRef.current; y++) {
            for (let x = 0; x < resolution && isMountedRef.current; x++) {
              if (!hasData[y][x]) continue;

              const elevation = elevationGrid[y][x];
              if (elevation === null || isNaN(elevation)) continue;

              const xPos = (x / (resolution - 1) - 0.5) * 80;
              const zPos = (y / (resolution - 1) - 0.5) * 80;
              const yPos =
                ((elevation - minElevation) / (maxElevation - minElevation)) *
                40;

              if (!isFinite(xPos) || !isFinite(yPos) || !isFinite(zPos)) {
                continue;
              }

              const vertexIndex = positions.length / 3;
              positions.push(-xPos, yPos, zPos);

              try {
                const color = getColorForElevation(
                  elevation,
                  minElevation,
                  maxElevation
                );
                colors.push(color.r, color.g, color.b);
              } catch (colorError) {
                colors.push(0.5, 0.5, 0.5); // Fallback to gray
              }

              vertexMap.set(`${x},${y}`, vertexIndex);
            }
          }

          if (!isMountedRef.current) return null;

          if (positions.length === 0) {
            throw new Error('No valid vertices generated');
          }

          // Second pass: create triangles
          for (let y = 0; y < resolution - 1 && isMountedRef.current; y++) {
            for (let x = 0; x < resolution - 1 && isMountedRef.current; x++) {
              const hasTopLeft = hasData[y][x];
              const hasTopRight = hasData[y][x + 1];
              const hasBottomLeft = hasData[y + 1][x];
              const hasBottomRight = hasData[y + 1][x + 1];

              if (hasTopLeft && hasTopRight && hasBottomLeft) {
                const topLeft = vertexMap.get(`${x},${y}`);
                const topRight = vertexMap.get(`${x + 1},${y}`);
                const bottomLeft = vertexMap.get(`${x},${y + 1}`);
                if (
                  topLeft !== undefined &&
                  topRight !== undefined &&
                  bottomLeft !== undefined
                ) {
                  indices.push(topLeft, bottomLeft, topRight);
                }
              }

              if (hasTopRight && hasBottomLeft && hasBottomRight) {
                const topRight = vertexMap.get(`${x + 1},${y}`);
                const bottomLeft = vertexMap.get(`${x},${y + 1}`);
                const bottomRight = vertexMap.get(`${x + 1},${y + 1}`);
                if (
                  topRight !== undefined &&
                  bottomLeft !== undefined &&
                  bottomRight !== undefined
                ) {
                  indices.push(topRight, bottomLeft, bottomRight);
                }
              }
            }
          }

          if (!isMountedRef.current) return null;

          if (indices.length === 0) {
            throw new Error('No valid triangles generated');
          }

          // Create and optimize the geometry
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
          );
          geometry.setAttribute(
            'color',
            new THREE.Float32BufferAttribute(colors, 3)
          );
          geometry.setIndex(indices);

          // Compute normals for better lighting
          geometry.computeVertexNormals();
          geometry.computeBoundingSphere();

          // At the end, update the elevation range
          if (data?.minElevation && data?.maxElevation) {
            setElevationRange({
              min: parseFloat(data.minElevation),
              max: parseFloat(data.maxElevation),
            });
          }

          return {
            geometry,
            minElevation,
            maxElevation,
          };
        } catch (geometryError) {
          if (!isMountedRef.current) return null;

          const errorMessage =
            geometryError instanceof Error
              ? geometryError.message
              : 'Unknown error';
          throw new Error(`Error creating geometry: ${errorMessage}`);
        }
      } catch (error) {
        if (isMountedRef.current) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          onError(`Error processing MVT data: ${errorMessage}`);
        }
        return null;
      } finally {
        if (isMountedRef.current) {
          onLoadingChange(false);
          processingRef.current = false;
        }
      }
    },
    [
      fetchMVTData,
      getColorForElevation,
      onError,
      onLoadingChange,
      tileToLonLat,
      data,
    ]
  );

  // Load MVT data
  const loadMVTData = useCallback(async () => {
    if (!data) return;

    // Create grid parameters
    const bounds = {
      minLon: parseFloat(data.minLongitude!),
      maxLon: parseFloat(data.maxLongitude!),
      minLat: parseFloat(data.minLatitude!),
      maxLat: parseFloat(data.maxLatitude!),
    };
    const gridParams: GridParams = {
      ...bounds,
      minElevation: parseFloat(data.minElevation!),
      maxElevation: parseFloat(data.maxElevation!),
      gridSize: 60,
      zoom: 5,
      tileSize: 512,
      resolution: latticeResolution({
        ...bounds,
        cellWidthFeet: data.cellWidthFeet,
        cellHeightFeet: data.cellHeightFeet,
      }),
    };

    // Get the MVT URL
    const mvtUrl = data.href;

    try {
      const result = await processMVTData(mvtUrl, gridParams);

      if (!isMountedRef.current || !result) return;

      const { geometry } = result;

      // Create a temporary mesh to calculate bounds
      const tempMesh = new THREE.Mesh(geometry);
      const box = new THREE.Box3().setFromObject(tempMesh);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Update positions and scale
      if (isFinite(center.length()) && isFinite(size.length())) {
        const newPosition: [number, number, number] = [
          -center.x,
          -center.y,
          -center.z,
        ];

        const maxSize = Math.max(size.x, size.y, size.z);
        if (maxSize > 0 && isFinite(maxSize)) {
          const scale = 130 / maxSize;
          const verticalScale = scale * 1.0; // Use 1.0 for natural height without stretching

          const newScale: [number, number, number] = [
            scale,
            verticalScale,
            scale,
          ];

          // Update state for React Three Fiber rendering
          setSurfaceGeometry(geometry);
          setSurfacePosition(newPosition);
          setSurfaceScale(newScale);

          // Calculate new bounding box for grid
          tempMesh.position.set(...newPosition);
          tempMesh.scale.set(...newScale);
          const newBox = new THREE.Box3().setFromObject(tempMesh);
          setMeshBox(newBox);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        onError(`Error loading MVT data: ${errorMessage}`);
      }
    }
  }, [data, onError, processMVTData]);

  // Initialize when component mounts
  useEffect(() => {
    // Flag to track component mounted state
    isMountedRef.current = true;

    // Save a reference to the tile cache at the time the effect runs
    const currentTileCache = tileCache.current;

    // Load data
    loadMVTData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;

      // Clear tile cache using the captured reference
      if (currentTileCache) {
        currentTileCache.clear();
      }
    };
  }, [loadMVTData]);

  return {
    geometry: surfaceGeometry,
    meshBox,
    surfacePosition,
    surfaceScale,
    elevationRange,
  };
}

// Scene background setup
const SceneBackground: React.FC = () => {
  const { scene } = useThree();

  useEffect(() => {
    // Set up white background
    scene.background = new THREE.Color(0xffffff);
  }, [scene]);

  return null;
};

// Scene component to handle Three.js rendering
const GeologicalMapScene: React.FC<{
  data: StructureGridResponse;
  onLoadingChange: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onCompassRotation: (rotation: number) => void;
}> = ({ data, onLoadingChange, onError, onCompassRotation }) => {
  const { geometry, meshBox, surfacePosition, surfaceScale, elevationRange } =
    useGeometryLoader(data, onLoadingChange, onError);

  // Handle camera initialization
  const handleCameraInit = useCallback(
    (camera: THREE.Camera) => {
      // Set initial compass rotation based on camera position
      const initialRotation = Math.atan2(camera.position.x, camera.position.z);

      // Use throttled compass rotation
      const throttledFn = throttle((rotation: number) => {
        onCompassRotation(rotation);
      }, 100);

      throttledFn(initialRotation);
    },
    [onCompassRotation]
  );

  return (
    <>
      {/* Setup Scene */}
      <SceneBackground />
      <SceneSetup onInit={handleCameraInit} />

      {/* Lights */}
      <SceneLights />

      {/* Grid and Labels */}
      <GridLinesAndFrame box={meshBox} />
      {data.minElevation && data.maxElevation && (
        <ElevationLabels
          box={meshBox}
          minElevation={elevationRange.min}
          maxElevation={elevationRange.max}
        />
      )}

      {/* Surface Mesh */}
      <SurfaceMesh
        geometry={geometry || undefined}
        position={surfacePosition}
        scale={surfaceScale}
        minElevation={elevationRange.min}
        maxElevation={elevationRange.max}
      />

      {/* Controls */}
      <SceneControls onCompassRotation={onCompassRotation} />
    </>
  );
};

const EventsContent = ({
  structureGrid,
}: {
  structureGrid: StructureGridResponse;
}) => (
  <EventsPanel
    header={structureGrid.gridSurface}
    title="Structure Grid Management"
    helperText="Structure Grid Management allows you to share information about a structure grid."
    eventsHref={structureGrid.resources.events.href}
    commentsHref={structureGrid.resources.comments.href}
  />
);
// Main component
const GeologicalMap3D: React.FC = () => {
  const location = useLocation();
  const id = location.pathname
    .split('/')
    .filter((el) => el)
    .pop();
  const structureGridHref = `/structure_grids/${id}`;
  const structureGridAtom = useMemo(
    () => createResourceAtom<StructureGridResponse>(structureGridHref),
    [structureGridHref]
  );
  const structureGrid = useAtomValue(structureGridAtom);
  const refetchStructureGrid = useSetAtom(structureGridAtom);

  const [compassRotation, setCompassRotation] = useState<number>(Math.PI);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [isStructureGridProcessing, setIsStructureGridProcessing] =
    useState<boolean>(false);

  const [structureGridProcessingError, setStructureGridProcessingError] =
    useState<string | null>(null);
  const [isStructureGridValidating, setIsStructureGridValidating] =
    useState<boolean>(false);

  // Pusher effect for structure grid extraction
  useEffect(
    () => {
      if (!structureGrid?.data || 'error' in structureGrid.data) {
        return;
      }

      const data = structureGrid.data;
      const status = data.status;
      // Only subscribe if status is 'processing' or 'created' or 'failed'
      if (
        (status === 'processing' ||
          status === 'created' ||
          status === 'failed') &&
        data.resources?.notifications?.channel
      ) {
        const { channel, events } = data.resources.notifications;

        if (channel && events) {
          setIsStructureGridProcessing(true);
          setStructureGridProcessingError(null);

          const extractionEvent = events[0].name;
          const subscription = subscribe(channel);

          subscription.bind(
            extractionEvent,
            (pusherData: {
              status: 'processed' | 'failed';
              errorMessage?: string;
            }) => {
              if (pusherData.status === 'processed') {
                setIsStructureGridProcessing(false);
                setStructureGridProcessingError(null);
                refetchStructureGrid();
              } else if (pusherData.status === 'failed') {
                setIsStructureGridProcessing(false);
                setStructureGridProcessingError(
                  pusherData.errorMessage ??
                    'Failed to process structure grid extraction. Please try again.'
                );
                refetchStructureGrid();
              }
            }
          );

          return () => {
            subscription.unbind(extractionEvent);
          };
        }
      } else {
        setIsStructureGridProcessing(false);
        setStructureGridProcessingError(null);
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureGrid?.data]
  );

  // Pusher effect for structure grid validation
  useEffect(
    () => {
      if (!structureGrid?.data || 'error' in structureGrid.data) {
        return;
      }

      const data = structureGrid.data;

      if (
        data.validity === 'incomplete' &&
        data.resources?.notifications?.channel
      ) {
        const { channel, events } = data.resources.notifications;

        if (channel && events) {
          setIsStructureGridValidating(true);
          const validationEvent = events[1].name;
          const subscription = subscribe(channel);

          subscription.bind(
            validationEvent,
            (pusherData: {
              status: 'processed' | 'failed';
              errorMessage?: string;
            }) => {
              if (pusherData.status === 'processed') {
                setIsStructureGridValidating(false);
                refetchStructureGrid();
              }
            }
          );

          return () => {
            subscription.unbind(validationEvent);
          };
        }
      }
      return undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureGrid?.data]
  );

  // Convert structure grid data to edit format
  const getStructureGridEditData = useMemo<
    StructureGridEditData | undefined
  >((): StructureGridEditData | undefined => {
    if (!structureGrid?.data || 'error' in structureGrid.data) {
      return undefined;
    }

    return {
      id: structureGrid.data.id,
      gridSurface: structureGrid.data.gridSurface,
      basinName: structureGrid.data.basinName || '',
      geologicInterval: structureGrid.data.geologicInterval || '',
      owner: structureGrid.data.owner,
      source: structureGrid.data.source,
    };
  }, [structureGrid?.data]);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  const handleEditSave = () => {
    // Refetch the structure grid data to get updated information
    refetchStructureGrid();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  if (structureGrid?.loading) {
    return (
      <div className={style.loadingContainer}>
        <Loading
          description="Loading structure grid data..."
          withOverlay={false}
          small
        />
      </div>
    );
  }

  if (!structureGrid?.data) {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle="No structure grid data available"
        lowContrast
      />
    );
  }

  if ('error' in structureGrid.data) {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle={structureGrid.data.error as string}
        lowContrast
      />
    );
  }

  const isStructureGridNotEditable = structureGrid.data.hasCollection;
  // Type guard function
  const hasAllCoordinates = (
    data: StructureGridResponse
  ): data is StructureGridCoordinates => {
    return (
      Boolean(data.minLongitude) &&
      Boolean(data.maxLongitude) &&
      Boolean(data.minLatitude) &&
      Boolean(data.maxLatitude) &&
      Boolean(data.minElevation) &&
      Boolean(data.maxElevation)
    );
  };

  return (
    <div className={classNames(style.container)}>
      {structureGrid.data.geologicInterval && (
        <EventsContent structureGrid={structureGrid.data} />
      )}
      <PageHeader
        title={`${structureGrid.data.geologicInterval}`}
        breadcrumbs={[
          ['/structure_grids', 'Structure Grid Management'],
          [location.pathname, structureGrid.data.geologicInterval],
        ]}
        attributes={
          <StructureGridValidityPopover
            structureGridId={structureGrid.data.id.toString()}
            validity={
              isStructureGridProcessing || isStructureGridValidating
                ? null
                : structureGrid.data.validity
            }
          />
        }
        actions={
          <div className={style.actionsArea}>
            <EventsPanelOpenButton />
            <Button
              disabled={
                isStructureGridNotEditable ||
                !getStructureGridEditData ||
                structureGrid.data.status === 'created' ||
                structureGrid.data.status === 'processing'
              }
              renderIcon={Edit}
              onClick={handleEditClick}
              size="sm"
              data-testid="edit-button"
            >
              <span>Edit</span>
            </Button>
            <Button
              disabled={
                isStructureGridNotEditable ||
                structureGrid.data.status === 'created' ||
                structureGrid.data.status === 'processing'
              }
              kind="danger"
              renderIcon={Delete}
              size="sm"
              onClick={handleDeleteClick}
              data-testid="delete-button"
            >
              <span>Delete</span>
            </Button>
          </div>
        }
      />
      {/* Structure Grid Details Section */}
      <div className={style.section}>
        <h3 className={style.sectionTitle}>Structure Grid Details</h3>
        <StructureGridDetails data={structureGrid.data} />
      </div>
      {/* Processing Status Section */}
      {(isStructureGridProcessing ||
        structureGridProcessingError ||
        structureGrid.data.status === 'failed') && (
        <div className={style.section}>
          <div className={style.processingSection}>
            <StructureGridExtractionStatus
              icon={<EventsIcon />}
              heading="Structure Grid Management"
              helperText="Structure Grid Management allows you to share information about a Structure Grid."
              footerText={
                structureGrid.data.status === 'failed'
                  ? 'Structure grid extraction failed, open comments.'
                  : 'For Details on Structure Grid Extraction, open comments.'
              }
              status={
                structureGrid.data.status === 'failed' ? 'failed' : 'processing'
              }
            />
          </div>
        </div>
      )}
      {/* Missing Coordinates Warning or Map Section */}
      {!isStructureGridProcessing && !structureGridProcessingError && (
        <>
          {/* Show warning if coordinates are missing */}
          {!hasAllCoordinates(structureGrid.data) ? (
            <InlineNotification
              kind="warning"
              title="Missing Coordinates"
              subtitle="Structure grid is missing required coordinate values"
              lowContrast
            />
          ) : (
            /* Show map if all coordinates exist */
            <div className={style.section}>
              <div className={style.mapSection}>
                <h3 className={style.sectionTitle}>Map</h3>
                <div className={style.threeDContainer}>
                  {/* Show MVT loading status */}
                  {isLoading && !isStructureGridProcessing && (
                    <div className={style.loadingOverlay}>
                      <Loading
                        description="Loading MVT data..."
                        withOverlay={false}
                      />
                    </div>
                  )}

                  {/* Show MVT error */}
                  {error && !isStructureGridProcessing && (
                    <div className={style.errorOverlay}>{error}</div>
                  )}

                  <div className={style.axisBox}>
                    <div className={style.xAxis} />
                    <div className={style.yAxis} />
                    <div className={style.zAxis} />
                  </div>

                  {/* X-axis (Longitude) labels */}
                  <div className={style.xLabels}>
                    <div className={style.xLabelsContainer}>
                      <span>
                        {format1(parseFloat(structureGrid.data.minLongitude))}
                      </span>
                      <span>
                        {format1(parseFloat(structureGrid.data.maxLongitude))}
                      </span>
                    </div>
                  </div>
                  <div className={style.xTitle}>Longitude</div>

                  {/* Y-axis (Elevation) labels */}
                  <div className={style.yLabels}>
                    <div className={style.yLabelsContainer}>
                      <span>
                        {format0(
                          Math.round(
                            parseFloat(structureGrid.data.maxElevation)
                          )
                        )}
                      </span>
                      <span>
                        {format0(
                          Math.round(
                            parseFloat(structureGrid.data.minElevation)
                          )
                        )}
                      </span>
                    </div>
                  </div>
                  <div className={style.yTitle}>Elevation</div>

                  {/* Z-axis (Latitude) labels */}
                  <div className={style.zLabels}>
                    <div className={style.zLabelsContainer}>
                      <span>
                        {format1(parseFloat(structureGrid.data.maxLatitude))}
                      </span>
                      <span>
                        {format1(parseFloat(structureGrid.data.minLatitude))}
                      </span>
                    </div>
                  </div>
                  <div className={style.zTitle}>Latitude</div>

                  {/* Main 3D Scene */}
                  <Canvas
                    camera={{
                      fov: 40,
                      near: 0.1,
                      far: 1500,
                      position: [0, 300, -300],
                    }}
                    style={{ background: 'white' }}
                    gl={{
                      preserveDrawingBuffer: true,
                      antialias: true,
                      alpha: false,
                    }}
                  >
                    <GeologicalMapScene
                      data={structureGrid.data}
                      onLoadingChange={setIsLoading}
                      onError={setError}
                      onCompassRotation={setCompassRotation}
                    />
                  </Canvas>

                  {/* Use imported compass component */}
                  <Compass rotation={compassRotation} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {/* Edit Modal */}
      <EditStructureGridModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSave={handleEditSave}
        initialData={getStructureGridEditData}
      />
      {/* Delete Modal */}
      <DeleteStructureGridModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        gridSurface={structureGrid.data.gridSurface}
        structureGridId={structureGrid.data.id}
      />
    </div>
  );
};

export default GeologicalMap3D;
